import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import type {
  DocumentosCounts,
  DocumentosListParams,
  DocumentosListResponse,
  DocumentosResumen,
} from '@/types/documento'

function buildQuery(params: DocumentosListParams): string {
  const sp = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    if (Array.isArray(value)) {
      if (value.length > 0) sp.set(key, value.join(','))
    } else {
      sp.set(key, String(value))
    }
  })
  const qs = sp.toString()
  return qs ? `?${qs}` : ''
}

const DEFAULT_COUNTS: DocumentosCounts = {
  total: 0,
  EPP: 0,
  CAPACITACION: 0,
  ATS: 0,
  CHECK_CAMIONETA: 0,
  CHECK_MAQUINARIA: 0,
}

/**
 * Resumen en cero mientras la primera respuesta no llega (o si falla). Con
 * `ultima_sincronizacion: null` el estado vacío dice "todavía no hay
 * documentos" en vez de "no coinciden con los filtros", que es la lectura
 * correcta cuando no sabemos nada.
 */
const DEFAULT_RESUMEN: DocumentosResumen = {
  ultima_sincronizacion: null,
  sincronizados_hoy: 0,
  firmados_7d: 0,
  trabajadores_con_registros: 0,
  trabajadores_total: 0,
}

export function useDocumentosBandeja(params: DocumentosListParams) {
  const [data, setData] = useState<DocumentosListResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const key = JSON.stringify(params)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<DocumentosListResponse>(
        `/documentos${buildQuery(params)}`
      )
      setData(resp)
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Error al cargar documentos'
      setError(msg)
      setData(null)
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const counts: DocumentosCounts = data?.counts ?? DEFAULT_COUNTS
  const resumen: DocumentosResumen = data?.resumen ?? DEFAULT_RESUMEN

  return { data, loading, error, counts, resumen, refetch: fetchData }
}
